import { apiFetch } from './api';

export type UserRole = 'ADMIN' | 'MANAGER' | 'MEMBER' | 'VIEWER';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  active: boolean;
  avatarUrl: string | null;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface AuthSession {
  user: AuthUser;
  // `token` is the access token (spec naming); `accessToken` is an alias.
  token: string;
  accessToken: string;
  refreshToken: string;
}

// Default landing route after a user passes authentication (CLE-448): EVERY
// role — admins included — lands on the daily summary. Callers may still
// override this with a pending deep-link (the page the user tried to open
// before being sent to login).
export function postAuthLandingPath(): string {
  return '/daily-summary';
}

// Standard success envelope: { success: true, data: <T> }.
interface Envelope<T> {
  success: true;
  data: T;
}

// Self-signup: when SMTP is configured the account is created unverified and
// the response carries no tokens — the user must click the emailed link first.
export interface RegisterPendingVerification {
  user: AuthUser;
  verificationRequired: true;
}

export type RegisterResult = AuthSession | RegisterPendingVerification;

export function isPendingVerification(
  result: RegisterResult,
): result is RegisterPendingVerification {
  return 'verificationRequired' in result && result.verificationRequired === true;
}

export async function register(input: {
  email: string;
  password: string;
  name: string;
}): Promise<RegisterResult> {
  const res = await apiFetch<Envelope<RegisterResult>>('/api/auth/register', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return res.data;
}

export async function login(input: { email: string; password: string }): Promise<AuthSession> {
  const res = await apiFetch<Envelope<AuthSession>>('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return res.data;
}

export async function refresh(refreshToken: string): Promise<AuthSession> {
  const res = await apiFetch<Envelope<AuthSession>>('/api/auth/refresh', {
    method: 'POST',
    body: JSON.stringify({ refreshToken }),
  });
  return res.data;
}

export async function logout(accessToken: string): Promise<void> {
  await apiFetch<Envelope<{ loggedOut: boolean }>>('/api/auth/logout', {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

export async function fetchMe(accessToken: string): Promise<AuthUser> {
  const res = await apiFetch<Envelope<AuthUser>>('/api/auth/me', {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return res.data;
}

/** Set or remove (null) the current user's profile photo — a small data URL cropped in the browser. */
export async function updateAvatar(
  accessToken: string,
  avatarUrl: string | null,
): Promise<AuthUser> {
  const res = await apiFetch<Envelope<AuthUser>>('/api/auth/me/avatar', {
    method: 'PUT',
    headers: { Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify({ avatarUrl }),
  });
  return res.data;
}

/** Change the current user's password (requires the current password). */
export async function changePassword(
  accessToken: string,
  input: { currentPassword: string; newPassword: string },
): Promise<void> {
  await apiFetch<Envelope<{ changed: boolean }>>('/api/auth/change-password', {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify(input),
  });
}

/**
 * „Cere ștergerea contului” (Google Play): confirms the password; the account is
 * deactivated at once and the firm's admins are told. Sign out right after.
 */
export async function requestAccountDeletion(accessToken: string, password: string): Promise<void> {
  await apiFetch<Envelope<{ requested: boolean }>>('/api/auth/me/deletion-request', {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify({ password }),
  });
}

// ---------------------------------------------------------------------------
// Password reset / email verification / invite flows (SaaS hardening).
// All the endpoints below are public (no auth header).
// ---------------------------------------------------------------------------

export async function forgotPassword(email: string): Promise<void> {
  await apiFetch<Envelope<{ sent: boolean }>>('/api/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function resetPassword(token: string, password: string): Promise<void> {
  await apiFetch<Envelope<{ reset: boolean }>>('/api/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ token, password }),
  });
}

/** Consume the emailed verification token; returns a full session (auto-login). */
export async function verifyEmail(token: string): Promise<AuthSession> {
  const res = await apiFetch<Envelope<AuthSession & { verified: boolean }>>(
    '/api/auth/verify-email',
    { method: 'POST', body: JSON.stringify({ token }) },
  );
  return res.data;
}

export async function resendVerification(email: string): Promise<void> {
  await apiFetch<Envelope<{ sent: boolean }>>('/api/auth/resend-verification', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

/** Validate an invite token WITHOUT consuming it (for the greeting screen). */
export async function getInvite(token: string): Promise<{ email: string; name: string }> {
  const res = await apiFetch<Envelope<{ email: string; name: string }>>(
    `/api/auth/invite/${encodeURIComponent(token)}`,
  );
  return res.data;
}

/** Consume the invite token by setting the password; returns a full session. */
export async function acceptInvite(token: string, password: string): Promise<AuthSession> {
  const res = await apiFetch<Envelope<AuthSession>>('/api/auth/accept-invite', {
    method: 'POST',
    body: JSON.stringify({ token, password }),
  });
  return res.data;
}
